/**
 * Signed P&L bar chart — shared with P&L Over Time and Time Analysis.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Bar, BarChart, CartesianGrid, Cell, ReferenceLine, XAxis, YAxis } from "recharts";
import { useTheme } from "next-themes";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import { cn } from "@/lib/utils";
import {
  STATS_CHART_TOOLTIP_TITLE_CLASS,
  StatisticsChartTooltipPnlRow,
  statsChartTooltipContentClassName,
} from "./statisticsChartTooltip";
import { useDisplayCurrency } from "../../../../contexts/DisplayCurrencyContext";
import { formatDisplayCurrency } from "../../../../utils/formatDisplayCurrency";
import { STATS_FONT_FAMILY, useStatsCardHeadlineColor } from "./statisticsCardHeadline";
import {
  PLOT_WIDTH_FALLBACK,
  SignedPnlBar,
  type SignedPnLBarDatum,
  computeSymmetricYDomain,
  getSignedBarChartLayout,
  pnlBarFill,
  SIGNED_PNL_BAR_CHART_MARGIN,
  type SignedPnlBarChartMargin,
} from "./statsSignedPnlBarUtils";

const chartConfig = {
  pnl: { label: "Net P&L" },
} satisfies ChartConfig;

export interface SignedPnLBarChartProps {
  data: SignedPnLBarDatum[];
  className?: string;
  symmetricYAxis?: boolean;
  yPaddingFloor?: number;
  /** Fixed Y domain (e.g. `[0, 100]` for win rate). Takes precedence over `symmetricYAxis`. */
  yDomain?: [number, number];
  formatYAxis?: (value: number) => string;
  tooltipMetricLabel?: string;
  renderTooltipExtra?: (row: SignedPnLBarDatum) => React.ReactNode;
  chartMargin?: SignedPnlBarChartMargin;
  yAxisWidth?: number;
}

const SignedPnLBarChart: React.FC<SignedPnLBarChartProps> = ({
  data,
  className = "aspect-auto h-[350px] w-full [&_.recharts-wrapper]:!overflow-visible [&_.recharts-surface]:overflow-visible",
  symmetricYAxis = false,
  yPaddingFloor = 50,
  yDomain: yDomainProp,
  formatYAxis,
  tooltipMetricLabel = "Total P&L",
  renderTooltipExtra,
  chartMargin = SIGNED_PNL_BAR_CHART_MARGIN,
  yAxisWidth = 72,
}) => {
  const { resolvedTheme } = useTheme();
  const { displayCurrency } = useDisplayCurrency();
  const axisTickColor = useStatsCardHeadlineColor();
  const themeClass = resolvedTheme === "dark" ? "dark" : "";
  const chartWrapRef = useRef<HTMLDivElement>(null);
  const [plotWidth, setPlotWidth] = useState(PLOT_WIDTH_FALLBACK);

  useEffect(() => {
    const el = chartWrapRef.current;
    if (!el) {
      return;
    }

    const updateWidth = () => {
      const width = el.getBoundingClientRect().width;
      if (width > 0) {
        setPlotWidth(width);
      }
    };

    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const chartData = useMemo(
    () =>
      data.map((row) => ({
        ...row,
        fill: row.fill ?? pnlBarFill(row.pnl),
      })),
    [data]
  );

  const barLayout = useMemo(
    () => getSignedBarChartLayout(chartData.length, plotWidth),
    [chartData.length, plotWidth]
  );

  const yDomain = useMemo(() => {
    if (yDomainProp) {
      return yDomainProp;
    }
    if (!symmetricYAxis) {
      return undefined;
    }
    return computeSymmetricYDomain(
      chartData.map((row) => row.pnl),
      yPaddingFloor
    );
  }, [chartData, symmetricYAxis, yPaddingFloor, yDomainProp]);

  const formatAxisTick = formatYAxis ?? ((value: number) =>
    formatDisplayCurrency(value, displayCurrency, { locale: "en-US" }));

  if (chartData.length === 0) {
    return null;
  }

  return (
    <div
      ref={chartWrapRef}
      className={themeClass ? `h-full min-h-0 w-full ${themeClass}` : "h-full min-h-0 w-full"}
      style={{ fontFamily: STATS_FONT_FAMILY }}
    >
      <ChartContainer config={chartConfig} className={className} style={{ fontFamily: STATS_FONT_FAMILY }}>
        <BarChart
          accessibilityLayer
          data={chartData}
          barCategoryGap={barLayout.barCategoryGap}
          margin={chartMargin}
        >
          <CartesianGrid vertical={false} strokeDasharray="4 4" className="stroke-border/50" />
          <ReferenceLine y={0} className="stroke-border" strokeWidth={1} />
          <XAxis
            dataKey="label"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            minTickGap={32}
            interval="preserveStartEnd"
            padding={barLayout.xAxisPadding}
            tick={{ fill: axisTickColor, fontSize: 11 }}
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            width={yAxisWidth}
            domain={yDomain}
            tick={{ fill: axisTickColor, fontSize: 11 }}
            tickFormatter={formatAxisTick}
          />
          <ChartTooltip
            cursor={{ fill: "hsl(var(--muted))", opacity: 0.25 }}
            content={
              <ChartTooltipContent
                className={statsChartTooltipContentClassName("w-[200px]")}
                labelClassName={STATS_CHART_TOOLTIP_TITLE_CLASS}
                hideIndicator
                labelFormatter={(_value, payload) => {
                  const row = payload?.[0]?.payload as SignedPnLBarDatum | undefined;
                  return row?.label ?? "";
                }}
                formatter={(value, _name, item) => {
                  const row = item.payload as SignedPnLBarDatum;
                  const n = Number(value);
                  const color = row.fill ?? pnlBarFill(n);
                  const formatted =
                    formatYAxis?.(n) ??
                    formatDisplayCurrency(n, displayCurrency, { locale: "en-US" });
                  return (
                    <div className="grid w-full gap-1.5">
                      <StatisticsChartTooltipPnlRow
                        label={tooltipMetricLabel}
                        value={formatted}
                        color={color}
                      />
                      {renderTooltipExtra?.(row)}
                    </div>
                  );
                }}
              />
            }
          />
          <Bar dataKey="pnl" barSize={barLayout.barSize} shape={SignedPnlBar}>
            {chartData.map((entry) => (
              <Cell key={entry.label} fill={entry.fill} />
            ))}
          </Bar>
        </BarChart>
      </ChartContainer>
    </div>
  );
};

export default SignedPnLBarChart;
